/**
 * 图床域名解析与主机改写（不含 WebP）。
 *
 * - 线上（production / 配置为 v2）：将已知图床地址归一到 cdnimg-v2
 * - 本地 / 非生产：将已知图床地址归一到 cdnimg-test（或 IMAGE_CDN_BASE_URL）
 *
 * 展示图片请走 handleImgToWebp（内部会先做本模块改写再追加 webp）。
 * 视频等非图片 URL 只调用 rewriteCatalogCdnUrl。
 */

declare global {
  interface Window {
    /** 当前环境图床主机，供 legacy 脚本与 handleImgToWebp 对齐 */
    __WIKI_IMAGE_CDN_HOST__?: string;
    __WIKI_ASSET_PREFIX__?: string;
  }
}

function normalizeCdnBase(value: string) {
  return value.trim().replace(/\/$/, '');
}

function hostFromCdnBase(value: string) {
  const normalized = normalizeCdnBase(value)
    .replace(/^https?:\/\//i, '')
    .replace(/^\/\//, '');
  return normalized.split('/')[0] || '';
}

export const CDN_PROD_HOST = 'cdnimg-v2.gamekee.com';
export const CDN_TEST_HOST = 'cdnimg-test.gamekee.com';

/**
 * Public image CDN base.
 * Prefer IMAGE_CDN_BASE_URL / NEXT_PUBLIC_IMAGE_CDN_BASE_URL when set
 * (e.g. https://cdnimg-test.gamekee.com); otherwise production uses v2, else test.
 */
/** Browser/SPA-safe env read（Vite 无 Node `process`）。 */
function envTrim(name: keyof typeof ENV_SNAPSHOT): string | undefined {
  const value = ENV_SNAPSHOT[name]?.trim();
  return value || undefined;
}

/**
 * 必须用静态属性读取 process.env.*，再收入表；动态 process.env[name]
 * 会被 Vite/oxc define 打成 {}[name]，导致生产读不到 NODE_ENV。
 */
const ENV_SNAPSHOT = {
  NODE_ENV: process.env.NODE_ENV,
  NEXT_PUBLIC_IMAGE_CDN_BASE_URL: process.env.NEXT_PUBLIC_IMAGE_CDN_BASE_URL,
  IMAGE_CDN_BASE_URL: process.env.IMAGE_CDN_BASE_URL,
  NEXT_PUBLIC_ASSET_PREFIX: process.env.NEXT_PUBLIC_ASSET_PREFIX,
  ASSET_PREFIX: process.env.ASSET_PREFIX,
} as const;

export const catalogCdnBaseUrl = (() => {
  const fromEnv =
    envTrim('NEXT_PUBLIC_IMAGE_CDN_BASE_URL') || envTrim('IMAGE_CDN_BASE_URL');
  if (fromEnv) {
    const host = hostFromCdnBase(fromEnv);
    return host ? `//${host}` : `//${CDN_TEST_HOST}`;
  }
  return envTrim('NODE_ENV') === 'production'
    ? `//${CDN_PROD_HOST}`
    : `//${CDN_TEST_HOST}`;
})();

/** 当前生效的图床主机名（无协议），可供 legacy 脚本经 window 注入。 */
export const catalogCdnHost = hostFromCdnBase(catalogCdnBaseUrl);

/** 把测试/正式图床地址归一到当前环境，保留 URL 的协议与路径。 */
export function rewriteCatalogCdnUrl(url: string): string {
  if (!url || !catalogCdnHost) return url;
  return url.replace(
    /^(https?:\/\/|\/\/)(?:(?:cdnimg-v2|cdnimg-test|cdnimg(?:0[1-9])?)\.gamekee\.com|wiki-obs\.obs\.cn-north-1\.myhuaweicloud\.com)(?=\/|:|\?|#|$)/i,
    (_, protocol: string) => `${protocol}${catalogCdnHost}`,
  );
}

export function rewriteCatalogCdnUrls(
  urls: string[] | null | undefined,
): string[] {
  if (!Array.isArray(urls)) return [];
  return urls.map(rewriteCatalogCdnUrl);
}
